import { Subscription } from 'rxjs';
import {
  BoundingBoxChangeEvent,
  CallbackTypePair,
  ChildRemovedEvent,
  LoadingStageChangeEvent,
  MoveEvent,
  ProductTypeChangeEvent,
  VisibilityEvent,
} from 'src/app/configurator/product/interfaces/ProductEvents';
import { PreviewData } from 'src/app/configurator/product/product-configuration/services/preview-generation-controller.service';
import { Floorplan } from 'src/app/configurator/product/room-builder/room-configuration/models/floorplan';
import {
  Box3,
  Box3Helper,
  Material,
  Matrix3,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Object3DEventMap,
  Quaternion,
  Scene,
  Texture,
  Vector3,
} from 'three';
import { OBB } from 'three/examples/jsm/math/OBB';
import {
  AttributeTypeENUM,
  FlippingAxisEnum,
  ProductLoadingStageEnum,
  ProductTypeENUM,
  ProductVisibilityENUM,
  UserDataEnums,
} from '../../enums/ProductEnums';
import {
  CollisionDescriptorType,
  CollisionDescriptorUsageType,
  IAttributeValueAvailibility,
} from '../../interfaces/GenericInterfaces';
import { ProductRelationshipProductPair } from '../../interfaces/ProductRelationshipProductPair';
import { absoluteVector } from '../../utils';
import { ChildProductDTO } from '../DTOs/ChildProductDTO';
import { FloorplanDTO } from '../DTOs/FloorplanDTO';
import { ProductDTO } from '../DTOs/ProductDTO';
import { TransformationDataDTO } from '../DTOs/TransformationDataDTO';
import { Attribute, MaterialChange, ModelChange } from './Attribute';
import { AttributeSettings } from './AttributeSettings';
import {
  AttributeSettingsController,
  AttributeWithAttributeChildren,
} from './AttributeSettings/AttributeSettingsController';
import { AttributeValueOrNumber } from './AttributeSettings/AttributeValueInstances/AttributeValueInstance';
import { AttributeValue, MaterialValue } from './AttributeValue';
import { CollisionDescriptor, OBBCollisionDescriptor } from './CollisionDescriptor';
import { Connector } from './Connector';
import { ProductBlueprint } from './ProductBlueprint';
import { ProductRelationship } from './ProductRelationship';
import { ProductRelationshipGroup } from './ProductRelationshipGroup';
import { ProductState } from './ProductState';
import { Wall } from './Wall';
import { WallMovementConstraint } from './WallPositionConstraint';

export enum ProductEventsENUM {
  AttributeChangeEvent = 'AttributeChangeEvent',
  RotateEvent = 'RotateEvent',
  MoveEvent = 'MoveEvent',
  ChildAdded = 'ChildAdded',
  TypeChange = 'TypeChange',
  LoadingStageChange = 'LoadingStageChange',
  BoundingBoxChange = 'BoundingBoxChange',
  ChildRemoved = 'ChildRemoved',
  VisibilityChanged = 'VisibilityChanged',
  ModelLoadedEvent = 'ModelLoadedEvent',
}

export const ATTRIBUTES_THAT_ARENT_IMMIDIATELY_APPLIED: AttributeTypeENUM[] = [
  AttributeTypeENUM.MaterialChange,
  AttributeTypeENUM.ModelChange,
];

export enum AttributeEventsENUM {
  MaterialChangeEvent = 'MaterialChangeEvent',
  ModelChangeEvent = 'ModelChangeEvent',
  NumericChangeEvent = 'NumericChangeEvent',
  FlipEvent = 'FlipEvent',
}

export interface AttributeSetting {
  attributeValue: AttributeValueOrNumber | undefined;
  isApplied?: boolean;
  map: AttributeMap | undefined;
  priority?: number;
}

export type AttributeMap = Map<Attribute, AttributeSetting | undefined>;

export interface AttributePair {
  attribute: Attribute;
  attributeValue: AttributeValue | number;
  priority?: number;
}
export interface AttributeOptionalValue {
  attribute: Attribute;
  attributeValue: AttributeValue | number | undefined;
}

export interface NumericAttributePair {
  attribute: Attribute;
  numericValue: number;
}

export interface ProductRelationshipGroupPair {
  group: ProductRelationshipGroup;
  relationship: ProductRelationship;
}

export interface ConnectorData {
  connector: Connector;
  isParent: boolean;
}

export const NEW_PRODUCT = undefined;

export const SCALAR_SMALLER_BOX = -0.001; // scalar to how much expand (or shrink - negative) original boudning box of the product

export interface ProductEventMap extends Object3DEventMap {
  AttributeChangeEvent: { data: AttributePair };
  RotateEvent: { data: null };
  MoveEvent: MoveEvent;
  ChildAdded: { data: Product };
  TypeChange: ProductTypeChangeEvent;
  LoadingStageChange: LoadingStageChangeEvent;
  BoundingBoxChange: BoundingBoxChangeEvent;
  ChildRemoved: ChildRemovedEvent;
  VisibilityChanged: VisibilityEvent;
  ModelLoadedEvent: { data: null };
  // Add other custom events as needed
}
export class Product<T extends ProductEventMap = ProductEventMap> extends Object3D<T> {
  public static fromProductBlueprint(productBlueprint: ProductBlueprint, type?: ProductTypeENUM) {
    return new Product(undefined, productBlueprint, type, '');
  }

  public static fromDTO(productDTO: ProductDTO, availibleBlueprints: ProductBlueprint[]) {
    const foundProductBlueprint = availibleBlueprints.find(
      (blueprint) => blueprint.id === productDTO.productBlueprintId?.toString(),
    );

    if (!foundProductBlueprint) {
      throw new Error('Products productBlueprint cannot be null');
    }

    const product = new Product(
      productDTO.id?.toString(),
      foundProductBlueprint,
      productDTO.productType,
      productDTO.wallId,
      productDTO.transformationData.rotation,
    );

    if (productDTO.transformationData?.position) {
      product.position.set(
        productDTO.transformationData.position[0],
        productDTO.transformationData.position[1],
        productDTO.transformationData.position[2],
      );
    }

    if (productDTO.transformationData?.rotation) {
      product.rotation.set(
        productDTO.transformationData.rotation[0],
        productDTO.transformationData.rotation[1],
        productDTO.transformationData.rotation[2],
      );
    }

    return product;
  }

  private _id: string | undefined;
  private _productBlueprint: ProductBlueprint;
  private _modelLoadingSubscription: Subscription | null;
  protected _attributeSettings: AttributeSettingsController;
  private _eventListeners: CallbackTypePair[] = [];

  protected _productState: ProductState;

  public get loadingStage() {
    return this._productState.loadingStage;
  }

  public get isFlipped() {
    return this._productState.isFlipped;
  }

  private _attributeProductsMap: Map<Attribute, ProductRelationshipProductPair[]> = new Map<
    Attribute,
    ProductRelationshipProductPair[]
  >();

  protected _setLoadingStage(loadingStage: ProductLoadingStageEnum) {
    if (this._productState.loadingStage !== loadingStage) {
      this._productState.loadingStage = loadingStage;
      this.dispatchProductEvent(ProductEventsENUM.LoadingStageChange, {
        data: loadingStage,
      } as LoadingStageChangeEvent);
    }
  }

  public get visibility() {
    return this._productState.visibility;
  }

  public set visibility(visibility: ProductVisibilityENUM) {
    if (this._productState.visibility !== visibility) {
      this._productState.visibility = visibility;
      this.visible = this._productState.visibility !== ProductVisibilityENUM.Invisible;
      this.dispatchProductEvent(ProductEventsENUM.VisibilityChanged, { data: visibility } as VisibilityEvent);
    }
  }

  protected _productRelationshipGroupsReferences: Map<ProductRelationshipGroup, ProductRelationshipProductPair> =
    new Map<ProductRelationshipGroup, ProductRelationshipProductPair>();

  private _connectors: Map<Product, ConnectorData> = new Map<Product, ConnectorData>();

  public get connectors() {
    return this._connectors;
  }

  private _productType?: ProductTypeENUM;

  private _productModel?: Object3D;

  private _boundingBox?: Box3; // original bounding box of the object
  private _boundingBoxSmaller?: Box3; // smaller bounding box used to skip collision calculation errors
  private _selfBoundingBox?: Box3; //Bounding box of object without children
  private _centeredBoundingBoxSize = new Vector3(0, 0, 0);
  protected _centerOffset: Vector3 = new Vector3(0, 0, 0);

  private _floorplan: Floorplan;

  private _removable = true;

  private _currentRotation = 0;

  private _positionalGhost?: Product; //Ghost za namene prestavljanja z raymeasurements

  public get productId() {
    return this._id;
  }

  public get productBlueprint() {
    return this._productBlueprint;
  }

  public get productType() {
    return this._productType;
  }

  public set productType(value) {
    this._productType = value;
    this.dispatchProductEvent(ProductEventsENUM.TypeChange, { data: value } as ProductTypeChangeEvent);
  }

  public get attributeSettings() {
    return this._attributeSettings;
  }

  public get floorplan() {
    return this._floorplan;
  }

  public get productRelationshipGroupsReferences() {
    return this._productRelationshipGroupsReferences;
  }

  public get centeredBoundingBoxSize() {
    return this._centeredBoundingBoxSize.clone();
  }

  public materialLoadingSubscription: Map<MaterialChange, { subscription: Subscription; value: MaterialValue }> =
    new Map<MaterialChange, { subscription: Subscription; value: MaterialValue }>();

  public get boundingBox() {
    if (!this._boundingBox) {
      return this._productBlueprint.boundingBox.clone().applyMatrix4(this.matrix);
    } else {
      return this._boundingBox.clone().applyMatrix4(this.matrix);
    }
  }

  public set boundingBox(value) {
    this._setBoundingBox(value);
  }

  public get selfBoundingBox() {
    return this._selfBoundingBox?.clone().applyMatrix4(this.matrix);
  }

  public get centeredSelfBoundingBox() {
    return this._selfBoundingBox?.clone();
  }

  public get smallBoundingBox() {
    if (!this._boundingBoxSmaller) {
      return this._productBlueprint.boundingBox.clone().applyMatrix4(this.matrix).expandByScalar(SCALAR_SMALLER_BOX);
    } else {
      return this._boundingBoxSmaller.clone().applyMatrix4(this.matrix);
    }
  }

  public get positionalGhost() {
    return this._positionalGhost;
  }

  public getLocalBoundingBox() {
    if (!this._boundingBox) {
      return this._productBlueprint.boundingBox.clone();
    } else {
      return this._boundingBox.clone();
    }
  }

  public addConnector(connector: Connector, product: Product, isParent = false) {
    this._connectors.set(product, { connector: connector, isParent: isParent } as ConnectorData);
  }

  public removeConnector(product: Product) {
    this._connectors.delete(product);
  }

  //selfBoundingBox je boundingbox brez otrok poleg
  protected _setSelfBoundingBox(bbox: Box3) {
    if (this._selfBoundingBox && bbox.equals(this._selfBoundingBox)) return;
    this._selfBoundingBox = bbox;
  }

  protected _setBoundingBox(bbox: Box3) {
    if (this._boundingBox && bbox.equals(this._boundingBox)) return;
    const oldBBox = this._boundingBox ? this._boundingBox.clone() : undefined;
    this._boundingBox = bbox;
    this._boundingBoxSmaller = bbox.clone().expandByScalar(SCALAR_SMALLER_BOX);
    this.dispatchProductEvent(ProductEventsENUM.BoundingBoxChange, {
      data: {
        oldBBox: oldBBox,
        bBox: bbox,
      },
    } as BoundingBoxChangeEvent);
  }

  public getPreviewProductsRelationship(previewProduct: Product) {
    for (const [productRelationshipGroup, productRelationshipProductPair] of this
      ._productRelationshipGroupsReferences) {
      if (previewProduct === productRelationshipProductPair.product) {
        return {
          productRelationship: productRelationshipProductPair.productRelationship,
          productRelationshipGroup: productRelationshipGroup,
        };
      }
    }
    return undefined;
  }

  public addPositionalGhost(positionalGhost: Product) {
    this._positionalGhost = positionalGhost;
    this.add(positionalGhost);
  }

  public removePositionalGhost() {
    if (this._positionalGhost) {
      this.removeChildProduct(this._positionalGhost);
      this._positionalGhost = undefined;
    }
  }

  public notifyThatAttributeValueIsApplied(attribute: Attribute) {
    this._attributeSettings.setAttributeValueAsApplied(attribute);
    this._checkLoadingStage();
  }

  public _checkLoadingStage() {
    const myAttributesAreApplied = this._attributeSettings.checkIfAttributesAreApplied();
    let childrenHaveAppliedAttributes = true;

    for (const child of this.children) {
      if (child instanceof Product && child.loadingStage === ProductLoadingStageEnum.Loading) {
        childrenHaveAppliedAttributes = false;
      }
    }

    if (
      myAttributesAreApplied &&
      childrenHaveAppliedAttributes &&
      (!this._productBlueprint.modelUrl || (this._productModel && !this._productModel?.userData['isPlaceholder']))
    ) {
      this._setLoadingStage(ProductLoadingStageEnum.Finished);
    } else {
      this._setLoadingStage(ProductLoadingStageEnum.Loading);
    }

    if (this.parent instanceof Product) {
      this.parent._checkLoadingStage();
    }
  }

  public calculateFutureBoundingBox(
    targetLocation: Vector3,
    targetRotation: Quaternion | null = null,
    useSmallerBBox = false,
  ): Box3 {
    const targetMatrix = new Matrix4();
    if (targetRotation) {
      targetMatrix.makeRotationFromQuaternion(targetRotation); // Apply target rotation
    }
    targetMatrix.setPosition(targetLocation); // Apply target position

    return this._calculateFutureBoundingBox(targetMatrix, useSmallerBBox);
  }

  private _calculateFutureBoundingBox(targetMatrix: Matrix4, useSmallerBBox = false): Box3 {
    if (!this._boundingBox) {
      this._setBoundingBox(this._productBlueprint.boundingBox.clone());
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const boundingBox = useSmallerBBox ? this._boundingBoxSmaller!.clone() : this._boundingBox!.clone();

    boundingBox.applyMatrix4(targetMatrix);
    return boundingBox;
  }

  public calculateFutureCollisionDescriptors(
    targetLocation: Vector3,
    targetRotation: Quaternion | null = null,
    useSmallerBBox = false,
    usageType = CollisionDescriptorUsageType.DEFAULT,
  ): (OBB | Box3)[] {
    // Step 1: Create a new transformation matrix for the target position and rotation
    const targetRotationMatrix = new Matrix4(); // matrix that has only rotation in the parameters, by deafult is identity matrix
    if (targetRotation) {
      targetRotationMatrix.makeRotationFromQuaternion(targetRotation); // Apply target rotation
    }

    const targetRotationMatrix3 = new Matrix3().setFromMatrix4(targetRotationMatrix); // need to convert because obb rotation is saved in rotationMatrix3

    const targetMatrix = targetRotationMatrix.clone(); // matrix that has rotation and translation in the paramaters
    targetMatrix.setPosition(targetLocation); // Apply target position

    const finalDescriptors: (OBB | Box3)[] = [];
    const descriptors = this.getCollisionDescriptors(usageType, useSmallerBBox);

    if (descriptors && descriptors.length > 0) {
      for (const descriptor of descriptors) {
        if (!descriptor) continue;

        switch (descriptor.type) {
          case CollisionDescriptorType.OBB: {
            const obbDescriptor = descriptor as OBBCollisionDescriptor;
            finalDescriptors.push(
              obbDescriptor.getFutureOBB(targetRotationMatrix3, targetMatrix, this.scale, useSmallerBBox),
            );
            break;
          }
        }
      }
    } else {
      finalDescriptors.push(this._calculateFutureBoundingBox(targetMatrix, useSmallerBBox)); // calcaulte here
    }
    for (const child of this.children) {
      if (child instanceof Product && child.productType !== ProductTypeENUM.Preview) {
        const childDescriptors = child.calculateFutureCollisionDescriptors(
          targetRotation
            ? targetLocation.clone().add(child.position.clone().applyQuaternion(targetRotation))
            : targetLocation.clone().add(child.position.clone()), //otroku je treba applyjat rotacijo
          targetRotation ? child.quaternion.clone().multiply(targetRotation) : null, //treba je seštet lokacije, da bo targetlocation otroka pravilen
          useSmallerBBox,
          usageType,
        );
        if (childDescriptors)
          for (const childDescriptor of childDescriptors) {
            finalDescriptors.push(childDescriptor.applyMatrix4(child.matrix));
          }
      }
    }
    return finalDescriptors;
  }

  public get centeredBoundingBox() {
    if (!this._boundingBox) {
      return this._productBlueprint.boundingBox?.clone();
    } else {
      return this._boundingBox?.clone();
    }
  }

  public get smallCenteredBoundingBox() {
    if (!this._boundingBoxSmaller) {
      return this._productBlueprint.boundingBox?.clone().expandByScalar(SCALAR_SMALLER_BOX);
    } else {
      return this._boundingBoxSmaller.clone();
    }
  }

  public get centerOffset() {
    return this._centerOffset?.clone();
  }

  public set centerOffset(value) {
    this._centerOffset = value;
  }

  public get currentRotation() {
    return this._currentRotation;
  }

  public notifythatParentsPositionHasChanged(
    movedByUser = false,
    originallyMovedProduct: Product,
    previewData?: PreviewData,
  ) {
    this.dispatchProductEvent(ProductEventsENUM.MoveEvent, {
      data: { movedByUser: movedByUser, previewData: previewData, originallyMovedProduct },
    } as MoveEvent);
    for (const child of this.children) {
      if (child instanceof Product) {
        child.notifythatParentsPositionHasChanged(movedByUser, originallyMovedProduct, previewData);
      }
    }
  }

  public setPosition(position: Vector3, movedByUser = false, previewData?: PreviewData) {
    if (position.equals(this.position)) return;
    this.position.copy(position);
    this.updateMatrix();
    this.notifythatParentsPositionHasChanged(movedByUser, this, previewData);
  }

  private _innitialWallId: string | undefined = '';

  public get innitialWallId() {
    return this._innitialWallId;
  }

  public set innitialWallId(value) {
    this._innitialWallId = value;
  }

  private _currentWallMovementConstraint?: WallMovementConstraint;

  public get currentWallMovementConstraint() {
    return this._currentWallMovementConstraint;
  }

  public set currentWallMovementConstraint(wallMovementConstraint: WallMovementConstraint | undefined) {
    this._currentWallMovementConstraint = wallMovementConstraint;
  }

  private _wall?: Wall;
  private _clipBoxReference?: WallMovementConstraint;

  public get wall() {
    return this._wall;
  }

  public setWall(wall?: Wall) {
    if (wall === this.wall) return;
    this._wall = wall;
    for (const child of this.children) {
      if (child instanceof Product) {
        child.setWall(wall);
      }
    }
  }

  public get clipBoxReference() {
    return this._clipBoxReference;
  }

  public set clipBoxReference(clipBoxReference) {
    this._clipBoxReference = clipBoxReference;
  }

  public updateCenteredBoundingBoxSize() {
    if (!this._boundingBox) {
      this._productBlueprint.boundingBox?.getSize(this._centeredBoundingBoxSize);
      this._centeredBoundingBoxSize = absoluteVector(this._centeredBoundingBoxSize.clone().multiply(this.scale));
    } else {
      this._boundingBox?.getSize(this._centeredBoundingBoxSize);
      this._centeredBoundingBoxSize = absoluteVector(this._centeredBoundingBoxSize.clone().multiply(this.scale));
    }
  }

  constructor(
    id: string | undefined,
    productBlueprint: ProductBlueprint,
    type?: ProductTypeENUM,
    wallId?: string,
    rotation = [0, 0, 0],
    visibility = ProductVisibilityENUM.Visible,
  ) {
    super();
    this._id = id;
    this._productBlueprint = productBlueprint;
    this._productType = type;
    this._innitialWallId = wallId ? wallId : undefined;

    this._productState = new ProductState(undefined, visibility);

    this._floorplan = new Floorplan();

    const rotationVector = new Vector3(rotation[0], rotation[1], rotation[2]);

    let linkVector = new Vector3(0, 0, 1);

    if (
      productBlueprint.movementData.wallPositionConstraints.length > 0 &&
      this.productBlueprint.movementData.wallPositionConstraints[0].linkVector
    ) {
      linkVector = this.productBlueprint.movementData.wallPositionConstraints[0].linkVector;
      this._currentWallMovementConstraint = this.productBlueprint.movementData.wallPositionConstraints[0];
    }

    this._currentRotation = rotationVector.dot(linkVector);

    this._attributeSettings = new AttributeSettingsController(productBlueprint.attributes);

    this._attributeSettings.attributeWasSet$.subscribe((attributePair) =>
      this._attributeWasSet(attributePair.attribute, attributePair.attributeValue),
    );

    this._modelLoadingSubscription = this._productBlueprint.model$.subscribe((model) => {
      if (model) {
        this._onGLTFModelLoad(model);
      }
    });
  }

  private _onGLTFModelLoad(model: Object3D) {
    this.disposeModels();
    if (this._productModel) this.remove(this._productModel);
    const clonedObject = model.clone(true);
    clonedObject.traverse(function (child) {
      if (child instanceof Mesh) {
        child.material = child.material.clone();
        if (child.material instanceof Material) {
          child.material.userData[UserDataEnums.InitialTransparent] = child.material.transparent;
          child.material.userData[UserDataEnums.InitialOpacity] = child.material.opacity;
        }

        //console.log(child.material);
        child.geometry = child.geometry.clone();
      }
    });
    this._addModelShadows(clonedObject);
    this.add(clonedObject);
    this._productModel = clonedObject;
    this.updateBoundingBox();

    this._attributeSettings.reApplyAttributes();

    this._checkLoadingStage();

    this.dispatchProductEvent(ProductEventsENUM.ModelLoadedEvent, { data: null });
  }

  private _addModelShadows(model: Object3D) {
    model.traverse((child) => {
      if (child instanceof Mesh) {
        child.receiveShadow = true;
        child.castShadow = true;
      }
    });
  }

  private _customTraverse(object: Object3D, callback: (child: Object3D) => void, traverseAttributeChildren = false) {
    // If the current object is an instance of Product, stop traversing deeper

    if (
      object instanceof Product &&
      (object.productType === ProductTypeENUM.Child ||
        object.productType === ProductTypeENUM.LinkedPreview ||
        (object.productType === ProductTypeENUM.Attribute && !traverseAttributeChildren))
    ) {
      return;
    }

    // Call the callback on the current object
    callback(object);

    // If the object has children, traverse them
    if (object.children) {
      for (const child of object.children) {
        this._customTraverse(child, callback, traverseAttributeChildren);
      }
    }
  }

  protected dispatchProductEvent<K extends keyof T>(eventName: K, eventData: T[K]) {
    // glej jebiga
    // eslint-disable-next-line
    const event: any = { type: eventName, ...eventData };
    this.dispatchEvent(event);
  }

  public findWall(walls: Wall[]) {
    this._wall = walls.find((wall) => wall.wallId === this._innitialWallId);
  }

  //TODO: prestavi to v bazo
  public isChildMiddleAttributeSettingEnabled() {
    return true;
  }

  //TODO: prestavi to v bazo
  public isReapplyingToParentNeeded() {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public override addEventListener(type: string, callback: any) {
    super.addEventListener(type, callback);
    this._eventListeners.push({ function: callback, type: type });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public override removeEventListener(type: string, callback: any) {
    // Call the original removeEventListener from the superclass
    super.removeEventListener(type, callback);

    // Find the index of the listener in the array
    const index = this._eventListeners.findIndex(
      (listener) => listener.type === type && listener.function === callback,
    );

    // If the listener is found, remove it from the array
    if (index !== -1) {
      this._eventListeners.splice(index, 1);
    }
  }

  public removeAllEventListeners(): void {
    // Iterate over a copy of the array, since we'll be modifying the original array
    const listenersCopy = [...this._eventListeners];
    for (const listener of listenersCopy) {
      this.removeEventListener(listener.type, listener.function);
    }

    // The array should already be empty, but this is a safeguard
    this._eventListeners = [];
  }

  public unsubscribeFromModelChange() {
    this._modelLoadingSubscription?.unsubscribe();
  }

  //ONLY FOR DEBUGGING!
  public showBoundingBox(scene: Scene) {
    if (this.boundingBox) {
      scene.add(new Box3Helper(this.boundingBox, '#ff0000'));
    }
    for (const child of this.children) {
      if (child instanceof Product) {
        child.showBoundingBox(scene);
        /* if (childbb) {
          scene.add(new Box3Helper(childbb, '#ff0000'));
        } */
      }
    }
  }

  public applyMaterial(texture: Texture | null, materialNames: string[]) {
    this.traverseToProduct((child) => {
      if (child instanceof Mesh && materialNames.includes(child.material.name)) {
        child.material.map = texture;
      }
    });
  }

  public applyNormalMap(texture: Texture | null, materialNames: string[]) {
    this.traverse((child) => {
      if (child instanceof Mesh && materialNames.includes(child.material.name)) {
        child.material.normalMap = texture;

        /* child.material.normalMapType = ObjectSpaceNormalMap; */
      }
    });
  }

  public applyRoughnessMap(texture: Texture | null, materialNames: string[]) {
    this.traverse((child) => {
      if (child instanceof Mesh && materialNames.includes(child.material.name)) {
        child.material.roughnessMap = texture;

        /* child.material.normalMapType = ObjectSpaceNormalMap; */
      }
    });
  }

  public updateBoundingBox(recursive = true) {
    const bbox = this._productBlueprint.boundingBox?.clone() ? this._productBlueprint.boundingBox?.clone() : new Box3();
    if (!bbox) {
      return;
    }
    this._setSelfBoundingBox(this._productBlueprint.selfBoundingBox);

    // If you have a Box3Helper visualizing this Box3, you might need to update it
    for (const child of this.children) {
      if (child instanceof Product && child.visibility !== ProductVisibilityENUM.Invisible) {
        if (recursive) {
          child.updateBoundingBox(recursive);
        }
        const childbb = child.boundingBox;
        if (childbb) {
          bbox.union(childbb);
        }
      }
    }

    const bboxSize = new Vector3();
    const currentCenter = new Vector3();

    bbox.getCenter(currentCenter);
    bbox.getSize(bboxSize);

    this._centerOffset = currentCenter.multiply(this.scale);

    this._setBoundingBox(bbox);
    this.updateCenteredBoundingBoxSize();
    if (this.parent instanceof Product) this.parent.updateBoundingBox(false);
  }

  public setDefaultAttributes(overrideAttributePairs: AttributePair[] = []) {
    this._attributeSettings.setDefaultAttributeValues(overrideAttributePairs);
  }

  public setAttributesFromAttributeMap(attributeMap: AttributeMap) {
    attributeMap.forEach((attributeSetting, attribute) => {
      if (attributeSetting && attributeSetting.attributeValue) {
        this.setAttribute(attribute, attributeSetting.attributeValue, attributeSetting.priority);
        if (attributeSetting.map) {
          this._recursivelySetAttributesFromAttributeMap(attributeSetting.map);
        }
      }

      /* this._attributeSettings.set(attribute, attributeSetting); */
    });
  }

  private _recursivelySetAttributesFromAttributeMap(attributeMap: AttributeMap) {
    attributeMap.forEach((attributeSetting, attribute) => {
      if (attributeSetting && attributeSetting.attributeValue) {
        this.setAttribute(attribute, attributeSetting.attributeValue, attributeSetting.priority);
        if (attributeSetting.map) {
          this._recursivelySetAttributesFromAttributeMap(attributeSetting.map);
        }
      }
    });
  }

  public setAttributesFromAttributeSettings(attributeSettings: AttributeSettings[]) {
    for (const attributeSetting of attributeSettings) {
      if (attributeSetting.key && attributeSetting.value) {
        this.setAttribute(attributeSetting.key, attributeSetting.value, attributeSetting.priority);
      } else if (attributeSetting.key && attributeSetting.numericValue) {
        this.setAttribute(attributeSetting.key, attributeSetting.numericValue, attributeSetting.priority);
      }
    }
  }

  public checkIfProductAncestorHasProductBlueprint(productBlueprint: ProductBlueprint): boolean {
    if (this.productBlueprint === productBlueprint) return true;
    if (this.parent instanceof Product) return this.parent.checkIfProductAncestorHasProductBlueprint(productBlueprint);
    return false;
  }

  public checkIfAttributeCanBeSet(attribute: Attribute, attributeValue: AttributeValue | number) {
    return { isAvailible: true } as IAttributeValueAvailibility;
  }

  public setAttributeChildren(attribute: Attribute, products: ProductRelationshipProductPair[]) {
    this._attributeProductsMap.set(attribute, products);
  }

  public setAttributeChild(attribute: Attribute, product: ProductRelationshipProductPair) {
    const currentProductRelationshipProductPairs = this._attributeProductsMap.get(attribute);

    if (!currentProductRelationshipProductPairs) {
      this._attributeProductsMap.set(attribute, [product]);
      return;
    }

    currentProductRelationshipProductPairs.push(product);
  }

  public getAttributeChildren(attribute: Attribute) {
    const attributeChildren = this._attributeProductsMap.get(attribute);
    if (!attributeChildren) return [];
    return attributeChildren;
  }

  public getAllAttributeChildren() {
    const attributeWithAttributeChildren: AttributeWithAttributeChildren[] = [];
    for (const [attribute, attributeChildren] of this._attributeProductsMap) {
      attributeWithAttributeChildren.push({ attribute: attribute, children: attributeChildren });
    }
    return attributeWithAttributeChildren;
  }

  public setAttribute(attribute: Attribute, attributeValue?: AttributeValue | number, priority = 0) {
    if (attributeValue !== undefined) {
      this._attributeSettings.setAttribute(attribute, attributeValue, priority);
      this.disposeOfOrphans();
    }
  }

  public disposeOfOrphans() {
    for (const [attributeInMap, attributeChildren] of this._attributeProductsMap) {
      const found = this.recursivelyFindAttributeInAttributeMap(
        attributeInMap,
        this._attributeSettings.generateAttributeMap(),
      );
      if (!found) {
        for (const attributeChild of attributeChildren) {
          if (attributeInMap instanceof ModelChange && attributeInMap.visibilityChange) {
            attributeChild.product.visibility = ProductVisibilityENUM.Invisible;
          } else {
            this.removeChildProduct(attributeChild.product);
            this._attributeProductsMap.delete(attributeInMap);
          }
        }
      }
    }
  }

  public removeChildProduct(child: Product) {
    this.remove(child);
    child.dispose();
    this.dispatchProductEvent(ProductEventsENUM.ChildRemoved, { data: child } as ChildRemovedEvent);
  }

  public recursivelyFindAttributeInAttributeMap(attribute: Attribute, attributeMap: AttributeMap) {
    for (const [attributeOfAttributeMap, attributeSetting] of attributeMap) {
      if (attribute === attributeOfAttributeMap) return true;
      if (attributeSetting && attributeSetting.map) {
        const foundInChild = this.recursivelyFindAttributeInAttributeMap(attribute, attributeSetting.map);
        if (foundInChild) return true;
      }
    }
    return false;
  }

  protected _attributeWasSet(attribute: Attribute, attributeValue?: AttributeValue | number) {
    if (attribute !== undefined && attributeValue !== undefined) {
      this.dispatchProductEvent(ProductEventsENUM.AttributeChangeEvent, {
        data: { attribute: attribute as Attribute, attributeValue: attributeValue },
      });
      this._checkLoadingStage();
      return;
    }

    throw new Error(
      'Trying to set attribute in product ' + this.name + ' but type of attribute and attribute value do not match',
    );
  }

  public recursivelyFindAttributeChildren(attribute: Attribute, attributeSettings: AttributeMap) {
    for (const [attributeOfSettings, attributeSetting] of attributeSettings) {
      if (attribute === attributeOfSettings) return true;
      if (
        attributeSetting &&
        attributeSetting.map &&
        this.recursivelyFindAttributeChildren(attribute, attributeSetting.map)
      )
        return true;
    }
    return false;
  }

  public removeAttributeChild(attribute: Attribute, productRelationship: ProductRelationship) {
    let foundChildren = this.getAttributeChildren(attribute);
    if (!foundChildren) {
      foundChildren = [];
    }

    const removedChildren: Product[] = [];

    for (let i = foundChildren.length - 1; i >= 0; i--) {
      const child = foundChildren[i];
      if (child.productRelationship === productRelationship) {
        this.removeChildProduct(child.product);
        removedChildren.push(child.product);
        foundChildren.splice(i, 1); // Remove the child from the array
      }
    }

    return removedChildren;
  }

  public removeAttributeChildren(attribute: Attribute) {
    let foundChildren = this.getAttributeChildren(attribute);
    if (!foundChildren) {
      foundChildren = [];
    }

    for (const child of foundChildren) {
      this.removeChildProduct(child.product);
    }
    this._attributeProductsMap.delete(attribute);

    return foundChildren;
  }

  public getTopParentProduct() {
    let parent = this.parent as Product;
    while (parent instanceof Product && parent.productType === ProductTypeENUM.Attribute) {
      parent = parent.parent as Product;
    }

    return parent;
  }

  public removeChildProductReference(product: Product) {
    this._productRelationshipGroupsReferences;
    for (const [productRelationshipGroup, productRelationshipProductPair] of this
      ._productRelationshipGroupsReferences) {
      if (productRelationshipProductPair.product === product) {
        this._productRelationshipGroupsReferences.delete(productRelationshipGroup);
      }
    }
  }

  public dispose() {
    if (this.parent instanceof Product) {
      if (this._productType === ProductTypeENUM.Child || ProductTypeENUM.PositionalGhost) {
        this.parent.removeChildProductReference(this);
      }
    }

    for (const child of [...this.children]) {
      if (child && child instanceof Product) {
        this.removeChildProduct(child);
      }
    }

    this._modelLoadingSubscription?.unsubscribe();
    this.disposeModels();
    this.removeFromParent();
    this.removeAllEventListeners();

    this.removeAllChildren();
  }

  public addProduct(product: Product) {
    this.add(product);
    this.updateBoundingBox();
  }

  public getAvailibleProductRelationShipGroups() {
    const productRelationShipGroups = this.getProductRelationshipGroups();
    const availibleProductRelationShipGroups: ProductRelationshipGroup[] = [];

    for (const productRelationShipGroup of productRelationShipGroups) {
      const productRelationshipProductPair = this._productRelationshipGroupsReferences.get(productRelationShipGroup);

      if (!productRelationshipProductPair || productRelationshipProductPair.product.isGhost()) {
        availibleProductRelationShipGroups.push(productRelationShipGroup);
      }
    }

    return availibleProductRelationShipGroups;
  }

  public getProductRelationshipGroups(productToPreview?: Product) {
    return this.productBlueprint.productRelationshipGroups;
  }

  public isGhost() {
    return this._productState.visibility === ProductVisibilityENUM.Ghost;
  }

  public addChild(
    product: Product,
    productRelationship: ProductRelationship,
    productRelationshipGroup: ProductRelationshipGroup,
  ) {
    this._productRelationshipGroupsReferences.set(productRelationshipGroup, {
      product: product,
      productRelationship: productRelationship,
    });
    this.add(product);
    this.updateBoundingBox();
    this.dispatchProductEvent(ProductEventsENUM.ChildAdded, { data: product });
    product.addEventListener(ProductEventsENUM.LoadingStageChange, (event: LoadingStageChangeEvent) => {
      this._checkLoadingStage();
    });
  }

  public addAttributeChildren(children: ProductRelationshipProductPair[], attribute: Attribute) {
    for (const child of children) {
      this.addProduct(child.product);
    }

    this.setAttributeChildren(attribute, children);
    for (const child of children) {
      child.product.addEventListener(ProductEventsENUM.LoadingStageChange, (event: LoadingStageChangeEvent) => {
        this._checkLoadingStage();
      });
    }
  }

  public addAttributeChild(child: ProductRelationshipProductPair, attribute: Attribute) {
    this.addProduct(child.product);

    this.setAttributeChild(attribute, child);

    child.product.addEventListener(ProductEventsENUM.LoadingStageChange, (event: LoadingStageChangeEvent) => {
      this._checkLoadingStage();
    });
  }

  public flip(flippingAxis: FlippingAxisEnum, value: number) {
    switch (flippingAxis) {
      case FlippingAxisEnum.X: {
        this.scale.setX(value);
        this._productState.isFlipped = this._productState.isFlipped ? false : true;
        break;
      }
      case FlippingAxisEnum.Y: {
        this.scale.setY(value);
        this._productState.isFlipped = this._productState.isFlipped ? false : true;
        break;
      }
      case FlippingAxisEnum.Z: {
        this.scale.setZ(value);
        this._productState.isFlipped = this._productState.isFlipped ? false : true;
        break;
      }
    }

    this.updateMatrix();
    this.updateBoundingBox(true);
  }

  public disposeModels() {
    this.traverse((obj) => {
      if (obj instanceof Mesh) {
        obj.geometry.dispose();
        if (obj.material.length) {
          for (let i = 0; i < obj.material.length; ++i) {
            if (obj.material[i].map) {
              obj.material[i].map.dispose();
            }

            if (obj.material[i].lightMap) {
              obj.material[i].lightMap.dispose();
            }

            if (obj.material[i].bumpMap) {
              obj.material[i].bumpMap.dispose();
            }

            if (obj.material[i].normalMap) {
              obj.material[i].normalMap.dispose();
            }

            if (obj.material[i].specularMap) {
              obj.material[i].specularMap.dispose();
            }

            if (obj.material[i].envMap) {
              obj.material[i].envMap.dispose();
            }

            obj.material[i].dispose();
          }
        } else {
          if (obj.material.map) {
            obj.material.map.dispose();
          }

          if (obj.material.lightMap) {
            obj.material.lightMap.dispose();
          }

          if (obj.material.bumpMap) {
            obj.material.bumpMap.dispose();
          }

          if (obj.material.normalMap) {
            obj.material.normalMap.dispose();
          }

          if (obj.material.specularMap) {
            obj.material.specularMap.dispose();
          }

          if (obj.material.envMap) {
            obj.material.envMap.dispose();
          }

          obj.material.dispose();
        }

        //obj.removeFromParent();
      }
      /*  if (obj instanceof Product && obj !== this) {
        obj.dispose();
      } */
    });
  }

  public isRotatable(): boolean {
    return this.productBlueprint.rotateStep != null || this.productBlueprint.possibleRotations != null;
  }

  public rotate() {
    if (!this.isRotatable() || !this._currentWallMovementConstraint) {
      return;
    }

    if (this.productBlueprint.rotateStep) {
      this._rotateByStep(this._currentWallMovementConstraint, this.productBlueprint.rotateStep);
      return;
    }

    if (this.productBlueprint.possibleRotations) {
      this._rotateToNextPossibleRotation(this._currentWallMovementConstraint, this.productBlueprint.possibleRotations);
    }
  }

  private _rotateToNextPossibleRotation(
    currentWallMovementConstraint: WallMovementConstraint,
    possibleRotations: number[],
  ) {
    const currRotationIndex = possibleRotations.indexOf(this._currentRotation);

    if (currRotationIndex === -1) {
      return undefined;
    }

    const nextRotationIndex = (currRotationIndex + 1) % possibleRotations.length;
    const angle = possibleRotations[nextRotationIndex] - possibleRotations[currRotationIndex];

    if (angle !== 0) {
      this._rotateByStep(currentWallMovementConstraint, angle);
    }
  }

  private _rotateByStep(currentWallMovementConstraint: WallMovementConstraint, angleInRadians: number) {
    this._currentRotation = this._currentRotation + angleInRadians;

    if (this._currentRotation > 2 * Math.PI) {
      this._currentRotation = this._currentRotation - 2 * Math.PI;
    }

    if (!currentWallMovementConstraint.linkVector) return;

    this.rotateOnAxis(currentWallMovementConstraint.linkVector, angleInRadians);
    this.updateMatrixWorld(true);
    this.setGlobalMaterialsOffset();
    this.dispatchProductEvent(ProductEventsENUM.RotateEvent, { data: null });
  }

  public generateAttributeSettingsDTO() {
    return this._attributeSettings.generateAttributeSettingsDTO();
  }

  public generateChildrenProductDTOs(floorplanDTO?: FloorplanDTO) {
    const productChildrenDTO: ChildProductDTO[] = [];
    for (const [, productRelationshipProductPair] of this._productRelationshipGroupsReferences) {
      if (productRelationshipProductPair.product.visibility === ProductVisibilityENUM.Visible)
        productChildrenDTO.push(
          new ChildProductDTO(
            productRelationshipProductPair.productRelationship.id !== undefined
              ? Number(productRelationshipProductPair.productRelationship.id)
              : undefined,
            productRelationshipProductPair.product.toDTO(floorplanDTO),
          ),
        );
    }

    for (const attributeWithAttributeChildren of this.getAllAttributeChildren()) {
      if (attributeWithAttributeChildren.attribute instanceof ModelChange)
        for (const child of attributeWithAttributeChildren.children) {
          if (child.product.visibility === ProductVisibilityENUM.Visible)
            productChildrenDTO.push(new ChildProductDTO(Number(child.productRelationship.id), child.product.toDTO()));
        }
    }

    return productChildrenDTO;
  }

  public addModulesToProductChildrenDTO(productChildrenDTO: ChildProductDTO[]) {
    for (const child of this.children) {
      if (
        child instanceof Product &&
        (child.productType === ProductTypeENUM.Module || child.productType === ProductTypeENUM.Connector)
      ) {
        const childProduct = new ChildProductDTO(undefined, child.toDTO());
        childProduct.childProduct.productType = child.productType;
        productChildrenDTO.push(childProduct);
      }
    }
    return productChildrenDTO;
  }

  /*   public generate */

  public toDTO(floorplanDTO?: FloorplanDTO): ProductDTO {
    const attributeSettingsDTOs = this._attributeSettings.generateAttributeSettingsDTO();

    const productChildrenDTO = this.generateChildrenProductDTOs(floorplanDTO);

    this.addModulesToProductChildrenDTO(productChildrenDTO);

    return new ProductDTO(
      Number(this._id) || undefined,
      Number(this._productBlueprint.id),
      productChildrenDTO,
      new TransformationDataDTO(
        undefined,
        [this.position.x, this.position.y, this.position.z],
        [this.rotation.x, this.rotation.y, this.rotation.z],
      ),
      attributeSettingsDTOs,
      floorplanDTO || this.floorplan.toDTO(),
      this._wall?.wallId ? this._wall.wallId : this.innitialWallId,
      this.productType,
    );
  }

  public removeAllChildren() {
    // Remove all existing children
    while (this.children.length) {
      const childToRemove = this.children[0];
      this.remove(childToRemove);
      if (childToRemove instanceof Product) {
        this.removeChildProduct(childToRemove);
      }
    }
  }

  public traverseToProduct(callback: (child: Object3D) => void, traverseAttributeChildren = false) {
    //this.customTraverse(this, callback);
    for (const child of this.children) {
      this._customTraverse(child, callback, traverseAttributeChildren);
    }
    /* this._customTraverse(this, callback); */
  }

  public isAncestor(productToCheck: Product) {
    if (productToCheck === this) return true;
    let parent = this.parent;
    while (parent !== null) {
      if (productToCheck === parent) return true;
      parent = parent.parent;
    }
    return false;
  }

  public getParentModule() {
    if (this._productType === ProductTypeENUM.Module) {
      return this;
    }

    let parent = this.parent;
    if (!parent) {
      return this;
    }

    while (!(parent instanceof Product) || (parent as Product).productType !== ProductTypeENUM.Module) {
      if (!parent) {
        break;
      }
      parent = parent?.parent;
    }

    if (!parent) return this;

    return parent as Product;
  }

  public getHalfBBClone() {
    return this.centeredBoundingBoxSize.clone().multiplyScalar(0.5);
  }

  protected _getGlobalMaterialOffsets(): {
    material: MeshStandardMaterial;
    worldPosition: Vector3;
    worldQuaternion: Quaternion;
  }[] {
    const arr: { material: MeshStandardMaterial; worldPosition: Vector3; worldQuaternion: Quaternion }[] = [];
    this.traverseToProduct((child) => {
      if (child instanceof Mesh) {
        if (child.material.userData['absoluteTextureVector']) {
          const worldPosition = new Vector3();
          const worldQuaternion = new Quaternion();
          child.getWorldPosition(worldPosition);
          child.getWorldQuaternion(worldQuaternion);
          arr.push({ material: child.material, worldPosition: worldPosition, worldQuaternion: worldQuaternion });
        }
      }
    });

    return arr;
  }

  public setGlobalMaterialOffset(
    material: MeshStandardMaterial,
    proudctWorldPosiiton?: Vector3,
    productWorldQuarternion?: Quaternion,
  ) {
    const origin = new Vector3(0, 0, 0); // Origin point on the plane
    if (proudctWorldPosiiton === undefined) {
      proudctWorldPosiiton = new Vector3();
      this.getWorldPosition(proudctWorldPosiiton);
    }

    if (productWorldQuarternion === undefined) {
      productWorldQuarternion = new Quaternion();
      this.getWorldQuaternion(productWorldQuarternion);
    }

    const normal = material.userData['absoluteTextureVector'].clone();
    //const normal = new Vector3(0, 0, 1);
    normal.applyQuaternion(productWorldQuarternion);
    // Create a vector that is perpendicular to the normal vector
    /* let up = new Vector3(0, 1, 0);
    if (normal.equals(up) || normal.equals(up.clone().negate())) {
      up = new Vector3(1, 0, 0);
    }
 */
    // Create a transformation matrix that aligns the normal vector with the z-axis
    /* const zAxis = up;
    const yAxis = new Vector3().crossVectors(normal, zAxis).normalize();
    const xAxis = new Vector3().crossVectors(zAxis, yAxis).normalize();

    const rotationMatrix = new Matrix4().makeBasis(xAxis, yAxis, zAxis);

    // Create a matrix that translates the origin to the given point
    const translationMatrix = new Matrix4().makeTranslation(-origin.x, -origin.y, -origin.z);

    // Combine the translation and rotation matrices
    const transformationMatrix = new Matrix4().multiplyMatrices(rotationMatrix, translationMatrix); */

    const quaternion = new Quaternion();
    quaternion.setFromUnitVectors(normal, new Vector3(0, 1, 0));

    // Apply the transformation matrix to the point
    //const transformedPoint = proudctWorldPosiiton.clone().applyMatrix4(transformationMatrix);
    const transformedPoint = proudctWorldPosiiton.clone().applyQuaternion(quaternion);

    material.map?.offset.set(transformedPoint.x * material.map.repeat.x, transformedPoint.z * material.map.repeat.y);
    material.normalMap?.offset.set(
      transformedPoint.x * material.normalMap.repeat.x,
      transformedPoint.z * material.normalMap.repeat.y,
    );
    material.bumpMap?.offset.set(
      transformedPoint.x * material.bumpMap.repeat.x,
      transformedPoint.z * material.bumpMap.repeat.y,
    );
    material.roughnessMap?.offset.set(
      transformedPoint.x * material.roughnessMap.repeat.x,
      transformedPoint.z * material.roughnessMap.repeat.y,
    );
  }

  public setGlobalMaterialsOffset() {
    const proudctWorldPosiiton = new Vector3();
    let productWorldQuarternion = new Quaternion();
    this.getWorldPosition(proudctWorldPosiiton);
    this.getWorldQuaternion(productWorldQuarternion);
    productWorldQuarternion = productWorldQuarternion.conjugate();

    for (const materialData of this._getGlobalMaterialOffsets()) {
      this.setGlobalMaterialOffset(materialData.material, materialData.worldPosition, materialData.worldQuaternion);
    }

    // update children products as well
    for (const child of this.children) {
      if (child instanceof Product) {
        child.setGlobalMaterialsOffset();
      }
    }
  }

  public getProductRelationshipGroupFromProduct(product: Product): ProductRelationshipGroupPair | null {
    for (const [group, productRelationshipPair] of this._productRelationshipGroupsReferences) {
      if (productRelationshipPair.product === product) {
        return {
          group: group,
          relationship: productRelationshipPair.productRelationship,
        } as ProductRelationshipGroupPair;
      }
    }
    return null;
  }

  public getCollisionDescriptors(
    usageType: CollisionDescriptorUsageType = CollisionDescriptorUsageType.DEFAULT,
    useSmallerCollisionDescriptors = false,
  ): CollisionDescriptor[] | undefined {
    const descriptors = useSmallerCollisionDescriptors
      ? this._productBlueprint.smallerCollisionDescriptors
      : this._productBlueprint.collisionDescriptors;
    const foundColisionDescriptors = descriptors.filter((descriptor) => descriptor.usageTypes.includes(usageType));
    if (foundColisionDescriptors.length === 0) {
      return descriptors.filter((collisionDescriptor) =>
        collisionDescriptor.usageTypes.includes(CollisionDescriptorUsageType.DEFAULT),
      );
    }
    return foundColisionDescriptors;
  }

  public getSavedGroupRelationship(group: ProductRelationshipGroup) {
    return this._productRelationshipGroupsReferences.get(group)?.productRelationship;
  }

  public isRemovable() {
    return this._removable;
  }

  public setIsRemovable(removable: boolean) {
    this._removable = removable;
  }
}
